import classNames from 'classnames';
import React, { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { CREATE_ORDER_SIDE, DEPTH } from 'src/common/constants';
import { currencyFormatter, DateTimeFormatter } from 'src/common/helpers';
import { Button } from 'src/components/core/button';
import { FlexContainer } from 'src/components/core/layout/FlexContainer';
import { StyledCards } from 'src/components/features/common/card/styledCards';
import { useAppDispatch, useAppSelector } from 'src/hooks';
import { cancelActiveOrder } from 'src/pages/spot/_redux/spotPageActions';
import { ActiveOrder, Symbol, OrderTableContentProps } from 'src/types';

const ActiveOrdersCard: FC<OrderTableContentProps> = ({ isShowAll }) => {
    const { t } = useTranslation();
    const [filterActiveOrders, setFilterActiveOrders] = useState([] as Array<ActiveOrder>);
    const currentSymbol: Symbol = useAppSelector((state) => state.spotPage.currentSymbol);
    const symbolId = currentSymbol.symbolId;
    const activeOrders: Array<ActiveOrder> = useAppSelector(
        (state) => state.spotPage.activeOpenOrders,
    );
    const symbols = useAppSelector((state) => state.globalData.configV2.symbol);
    const dispatch = useAppDispatch();

    useEffect(() => {
        let filtered = isShowAll
            ? activeOrders
            : activeOrders.filter((activeOrder) => activeOrder.symbolId === symbolId);
        filtered = [...filtered].sort((a, b) => (a.time - b.time > 0 ? -1 : 1));
        setFilterActiveOrders(filtered);
    }, [activeOrders, symbolId, isShowAll]);

    const cancelOrder = (orderId: string) => {
        dispatch(
            cancelActiveOrder({
                order_id: orderId,
                client_order_id: new Date().getTime(),
            }),
        );
    };

    const columns = [
        {
            title: t('spot_pairs'),
            key: 'spotPairs',
            width: '4%',
            render: (order: ActiveOrder) => {
                return (
                    <span>
                        {order.baseTokenId}
                        <span className="greyText">/{order.quoteTokenId}</span>
                    </span>
                );
            },
        },
        {
            title: t('order_type'),
            key: 'orderType',
            render: (order: ActiveOrder) => {
                return <span>{t(order.type.toLowerCase())}</span>;
            },
        },
        {
            title: t('direction'),
            key: 'direction',
            render: (order: ActiveOrder) => {
                return (
                    <span
                        className={classNames(
                            {
                                greenText: order.side === CREATE_ORDER_SIDE.BUY,
                            },
                            {
                                redText: order.side === CREATE_ORDER_SIDE.SELL,
                            },
                        )}
                    >
                        {t(order.side.toLowerCase())}
                    </span>
                );
            },
        },
        {
            title: t('order_value'),
            key: 'orderValue',
            render: (order: ActiveOrder) => {
                const orderValue = order.price * order.origQty;
                return (
                    <span>
                        {currencyFormatter(
                            orderValue,
                            DEPTH[symbols[order.symbolId].quotePrecision],
                        )}{' '}
                        <span className="greyText">{order.quoteTokenId}</span>
                    </span>
                );
            },
        },
        {
            title: t('order_price'),
            key: 'orderPrice',
            render: (order: ActiveOrder) => {
                return (
                    <span>
                        {currencyFormatter(
                            order.price,
                            DEPTH[symbols[order.symbolId].minPricePrecision],
                        )}{' '}
                        <span className="greyText">{order.quoteTokenId}</span>
                    </span>
                );
            },
        },
        {
            title: t('order_qty'),
            key: 'orderQty',
            render: (order: ActiveOrder) => {
                return (
                    <span>
                        {currencyFormatter(
                            order.origQty,
                            DEPTH[symbols[order.symbolId].basePrecision],
                        )}{' '}
                        <span className="greyText">{order.baseTokenId}</span>
                    </span>
                );
            },
        },
        {
            title: t('filled_qty'),
            key: 'filledQty',
            render: (order: ActiveOrder) => {
                return (
                    <span>
                        {currencyFormatter(
                            order.executedQty,
                            DEPTH[symbols[order.symbolId].basePrecision],
                        )}{' '}
                        <span className="greyText">{order.baseTokenId}</span>
                    </span>
                );
            },
        },
        {
            title: t('unfilled_qty'),
            key: 'unfilledQty',
            render: (order: ActiveOrder) => {
                return (
                    <span>
                        {currencyFormatter(
                            order.origQty - order.executedQty,
                            DEPTH[symbols[order.symbolId].basePrecision],
                        )}{' '}
                        <span className="greyText">{order.baseTokenId}</span>
                    </span>
                );
            },
        },
        {
            title: t('order_time'),
            key: 'orderTime',
            render: (order: ActiveOrder) => {
                return <span>{DateTimeFormatter(Number(order.time))}</span>;
            },
        },
        {
            title: t('order_id'),
            key: 'orderId',
            render: (order: ActiveOrder) => {
                return <span>{order.orderId}</span>;
            },
        },
        {
            title: t('action'),
            key: 'action',
            render: (order: ActiveOrder) => {
                return (
                    <FlexContainer styles={{ justifyContent: 'start' }}>
                        <Button
                            variant="outlined"
                            className="action-button"
                            onClick={() => cancelOrder(order.orderId)}
                        >
                            {t('cancel')}
                        </Button>
                    </FlexContainer>
                );
            },
        },
    ];

    return <StyledCards columns={columns} data={filterActiveOrders} />;
};

export { ActiveOrdersCard };
